import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  Dumbbell,
  Clock,
  Users,
  CheckCircle2,
  XCircle,
  AlertCircle,
} from "lucide-react";
import "../../assets/styles/GymStatus.css";

export default function GymStatus() {
  const navigate = useNavigate();

  // 1. State for Gender (Toggle)
  const [selectedGender, setSelectedGender] = useState("Male");

  // 2. State for Time & Status
  const [now, setNow] = useState(new Date());
  const [status, setStatus] = useState({
    isOpen: false,
    message: { ar: "", en: "" },
    nextSlot: { ar: "", en: "" },
  });

  // 3. Live Clock Update
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // 4. The CORE Logic (حساب الحالة - الخميس والجمعة إجازة)
  useEffect(() => {
    const calculateStatus = () => {
      const day = now.getDay(); // 0 = Sunday, 3 = Wed, 4 = Thursday, 5 = Friday
      const hour = now.getHours();

      // الخميس (4) والجمعة (5) مغلق دائماً
      if (day === 4 || day === 5) {
        setStatus({
          isOpen: false,
          message: { ar: "عطلة (الخميس والجمعة)", en: "Weekend (Thu & Fri)" },
          nextSlot: {
            ar: "يفتح السبت 7:00 صباحاً",
            en: "Opens Saturday 7:00 AM",
          },
        });
        return;
      }

      // متغير ذكي لمعرفة اليوم التالي
      let nextDayAr = day === 3 ? "السبت" : "غداً";
      let nextDayEn = day === 3 ? "Saturday" : "Tomorrow";

      // فترات اليوم (Slots)
      let isOpen = false;
      let message = { ar: "مغلق حالياً", en: "Currently Closed" };
      let nextSlot = { ar: "", en: "" };

      if (hour >= 7 && hour < 12) {
        // الفترة الصباحية (رجالي)
        if (selectedGender === "Male") {
          isOpen = true;
          message = {
            ar: "الفترة الصباحية (رجال)",
            en: "Morning Session (Men)",
          };
          nextSlot = { ar: "يغلق 12:00 ظهراً", en: "Closes 12:00 PM" };
        } else {
          isOpen = false;
          message = { ar: "مخصص للرجال فقط", en: "Men's Time Only" };
          nextSlot = {
            ar: "فترة السيدات 12:00 ظهراً",
            en: "Women start 12:00 PM",
          };
        }
      } else if (hour >= 12 && hour < 16) {
        // فترة الظهيرة (نسائي)
        if (selectedGender === "Female") {
          isOpen = true;
          message = { ar: "فترة السيدات", en: "Ladies Session" };
          nextSlot = { ar: "يغلق 4:00 عصراً", en: "Closes 4:00 PM" };
        } else {
          isOpen = false;
          message = { ar: "مخصص للسيدات فقط", en: "Women's Time Only" };
          nextSlot = { ar: "فترة الرجال 4:00 عصراً", en: "Men start 4:00 PM" };
        }
      } else if (hour >= 16 && hour < 22) {
        // الفترة المسائية (رجالي)
        if (selectedGender === "Male") {
          isOpen = true;
          message = {
            ar: "الفترة المسائية (رجال)",
            en: "Evening Session (Men)",
          };
          nextSlot = { ar: "يغلق 10:00 مساءً", en: "Closes 10:00 PM" };
        } else {
          isOpen = false;
          message = { ar: "مخصص للرجال فقط", en: "Men's Time Only" };
          nextSlot = {
            ar: `يفتح ${nextDayAr} 12:00 ظهراً`,
            en: `Opens ${nextDayEn} 12:00 PM`,
          };
        }
      } else {
        // مغلق (ليل متأخر أو فجر)
        isOpen = false;
        message = { ar: "الجيم مغلق الآن", en: "Gym is Closed" };
        nextSlot = {
          ar: `يفتح ${nextDayAr} 7:00 صباحاً`,
          en: `Opens ${nextDayEn} 7:00 AM`,
        };
      }

      setStatus({ isOpen, message, nextSlot });
    };

    calculateStatus();
  }, [now, selectedGender]);

  // Helper to format time nicely
  const formatTime = (date) => {
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  return (
    <div
      className="gym-page-wrapper"
      style={{
        marginTop: "40px",
        paddingTop: "70px",
        paddingBottom: "60px",
        minHeight: "100vh",
        backgroundColor: "#0f172a",
      }}
    >
      <div className="container d-flex flex-column align-items-center">
        {/* Main Wrapper عشان نلم المحتوى وميفرش على الشاشات الكبيرة */}
        <div className="w-100" style={{ maxWidth: "500px" }}>
          {/* Header (Back Button) */}
          <div className="d-flex align-items-center mb-4 gap-3 w-100">
            <button
              onClick={() => navigate(-1)}
              className="btn btn-dark rounded-circle p-0 shadow-sm border border-secondary border-opacity-25 d-flex align-items-center justify-content-center"
              style={{ width: "40px", height: "40px" }} // تم تصليح الخطأ هنا (justifyContent)
            >
              <ArrowLeft size={20} className="text-white" />
            </button>
            <div className="d-flex flex-column">
              <h2
                className="m-0 fw-bold title-gradient"
                dir="rtl"
                style={{ fontSize: "1.5rem" }}
              >
                حالة الجيم
              </h2>
              <span
                className="text-white-50 fw-medium"
                dir="ltr"
                style={{ fontSize: "0.85rem" }}
              >
                Gym Status
              </span>
            </div>
          </div>

          {/* Gender Toggle Switch */}
          <div className="gender-toggle d-flex justify-content-center gap-2 mb-4">
            <button
              className={`toggle-btn d-flex align-items-center justify-content-center gap-2 ${selectedGender === "Male" ? "active" : ""}`}
              onClick={() => setSelectedGender("Male")}
              style={{ padding: "10px 15px", borderRadius: "25px", flex: "1" }}
            >
              <Dumbbell size={18} />
              <div className="d-flex flex-column align-items-start lh-1">
                <span className="fw-bold" style={{ fontSize: "0.9rem" }}>
                  رجال
                </span>
                <span style={{ fontSize: "0.65rem", opacity: 0.8 }}>Men</span>
              </div>
            </button>
            <button
              className={`toggle-btn d-flex align-items-center justify-content-center gap-2 ${selectedGender === "Female" ? "active" : ""}`}
              onClick={() => setSelectedGender("Female")}
              style={{ padding: "10px 15px", borderRadius: "25px", flex: "1" }}
            >
              <Users size={18} />
              <div className="d-flex flex-column align-items-start lh-1">
                <span className="fw-bold" style={{ fontSize: "0.9rem" }}>
                  سيدات
                </span>
                <span style={{ fontSize: "0.65rem", opacity: 0.8 }}>Women</span>
              </div>
            </button>
          </div>

          {/* --- MAIN STATUS RING (ANIMATED) --- */}
          <motion.div
            className="status-ring-container mb-4"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            {/* صغرنا مقاس الدائرة شوية في CSS لو حبيت، بس هنا ظبطنا المحتوى الداخلي */}
            <div
              className={`status-ring ${status.isOpen ? "open" : "closed"}`}
            ></div>
            <div className="status-content d-flex flex-column align-items-center justify-content-center">
              <span className="status-icon mb-1">
                {status.isOpen ? (
                  <CheckCircle2 size={40} color="#10b981" />
                ) : (
                  <XCircle size={40} color="#ef4444" />
                )}
              </span>
              <div
                className="status-text d-flex flex-column align-items-center lh-1 mb-1"
                style={{ color: status.isOpen ? "#10b981" : "#ef4444" }}
              >
                <span className="fw-bold" style={{ fontSize: "1.3rem" }}>
                  {status.isOpen ? "مفتوح" : "مغلق"}
                </span>
                <span
                  className="fw-semibold"
                  style={{ fontSize: "0.85rem", opacity: 0.9 }}
                >
                  {status.isOpen ? "OPEN" : "CLOSED"}
                </span>
              </div>
              <div
                className="status-timer fw-bold text-white mt-1"
                dir="ltr"
                style={{ letterSpacing: "1px", fontSize: "1.1rem" }}
              >
                {formatTime(now)}
              </div>
            </div>
          </motion.div>

          {/* Status Message */}
          <div className="text-center mb-4 px-2 w-100">
            <h4
              className="text-white fw-bold mb-1"
              dir="rtl"
              style={{ fontSize: "1.2rem" }}
            >
              {status.message.ar}
            </h4>
            <h6
              className="text-white-50 fw-medium mb-3"
              dir="ltr"
              style={{ fontSize: "0.95rem" }}
            >
              {status.message.en}
            </h6>

            <div
              className="d-inline-flex flex-column flex-sm-row align-items-center justify-content-center gap-1 gap-sm-2 p-2 px-3 rounded-pill"
              style={{ background: "rgba(255,255,255,0.05)" }}
            >
              <div className="d-flex align-items-center gap-1 text-info">
                <Clock size={16} />
                <span
                  className="fw-bold"
                  dir="rtl"
                  style={{ fontSize: "0.85rem" }}
                >
                  {status.nextSlot.ar}
                </span>
              </div>
              <span className="d-none d-sm-inline text-info opacity-50">|</span>
              <span
                className="text-info"
                dir="ltr"
                style={{ fontSize: "0.8rem", opacity: 0.9 }}
              >
                {status.nextSlot.en}
              </span>
            </div>
          </div>

          {/* --- SCHEDULE TIMELINE --- */}
          <div className="schedule-container w-100">
            <div className="d-flex align-items-center justify-content-between mb-3 px-1">
              <h6 className="text-white fw-bold m-0" dir="rtl">
                جدول اليوم
              </h6>
              <span
                className="text-secondary text-uppercase fw-bold m-0"
                dir="ltr"
                style={{ fontSize: "0.75rem", letterSpacing: "1px" }}
              >
                Schedule
              </span>
            </div>

            <div className="d-flex flex-column gap-2">
              <ScheduleCard
                start={7}
                end={12}
                labelAr="الفترة الصباحية"
                labelEn="Morning"
                genderAr="رجال"
                genderEn="Men"
                cssType="men"
                currentHour={now.getHours()}
                day={now.getDay()}
              />
              <ScheduleCard
                start={12}
                end={16}
                labelAr="فترة الظهيرة"
                labelEn="Afternoon"
                genderAr="سيدات"
                genderEn="Women"
                cssType="women"
                currentHour={now.getHours()}
                day={now.getDay()}
              />
              <ScheduleCard
                start={16}
                end={22}
                labelAr="الفترة المسائية"
                labelEn="Evening"
                genderAr="رجال"
                genderEn="Men"
                cssType="men"
                currentHour={now.getHours()}
                day={now.getDay()}
              />
            </div>

            {/* Weekend Note (Thursday & Friday) */}
            {(now.getDay() === 4 || now.getDay() === 5) && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="alert alert-danger d-flex align-items-start text-start bg-danger bg-opacity-10 border-danger border-opacity-25 text-danger mt-3 rounded-4 p-2 gap-2"
              >
                <AlertCircle size={20} className="flex-shrink-0 mt-1" />
                <div className="d-flex flex-column">
                  <strong dir="rtl" style={{ fontSize: "0.85rem" }}>
                    الجيم مغلق طوال يومي الخميس والجمعة للراحة.
                  </strong>
                  <span dir="ltr" style={{ fontSize: "0.75rem", opacity: 0.9 }}>
                    Gym is closed on Thursdays & Fridays.
                  </span>
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Sub-component
function ScheduleCard({
  start,
  end,
  labelAr,
  labelEn,
  genderAr,
  genderEn,
  cssType,
  currentHour,
  day,
}) {
  const formatHour = (h) => (h > 12 ? h - 12 : h) + (h >= 12 ? " PM" : " AM");
  // لا يعمل الخميس ولا الجمعة
  const isActive =
    day !== 4 && day !== 5 && currentHour >= start && currentHour < end;

  return (
    <div
      className={`schedule-card d-flex align-items-center justify-content-between p-2 px-3 rounded-4 border ${isActive ? "border-primary border-opacity-50" : "border-white border-opacity-10"}`}
      style={{
        background: isActive
          ? "rgba(14, 165, 233, 0.1)"
          : "rgba(255, 255, 255, 0.03)",
        transition: "all 0.3s ease",
      }}
    >
      <div className="d-flex align-items-center gap-2 w-100">
        <div
          className={`p-2 rounded-circle flex-shrink-0 ${isActive ? "bg-primary text-white shadow-sm" : "bg-secondary bg-opacity-10 text-secondary"}`}
        >
          <Clock size={18} />
        </div>

        {/* التعديل هنا لضمان التوافق مع الموبايل بدون Overflow */}
        <div className="d-flex flex-row justify-content-between align-items-center w-100 gap-2">
          <div className="d-flex flex-column justify-content-center">
            <div
              className="fw-bold text-white mb-1"
              dir="ltr"
              style={{ fontSize: "0.9rem" }}
            >
              {formatHour(start)} - {formatHour(end)}
            </div>
            <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center lh-1 gap-1">
              <span
                className={`fw-semibold ${isActive ? "text-info" : "text-light"}`}
                dir="rtl"
                style={{ fontSize: "0.8rem" }}
              >
                {labelAr}
              </span>
              <span
                className="text-secondary small"
                dir="ltr"
                style={{ fontSize: "0.7rem" }}
              >
                {labelEn}
              </span>
            </div>
          </div>

          <div className="d-flex align-items-center flex-shrink-0">
            <div
              className={`d-flex flex-column align-items-center justify-content-center px-2 py-1 rounded-3 ${cssType === "men" ? "bg-primary bg-opacity-25 text-info" : "bg-pink bg-opacity-25 text-pink"}`}
              style={{
                minWidth: "60px",
                color: cssType === "women" ? "#ec4899" : "",
              }}
            >
              <span
                className="fw-bold lh-1 mb-1"
                dir="rtl"
                style={{ fontSize: "0.8rem" }}
              >
                {genderAr}
              </span>
              <span
                className="lh-1"
                style={{ fontSize: "0.6rem", opacity: 0.8 }}
                dir="ltr"
              >
                {genderEn}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}





// import React, { useState, useEffect } from "react";
// import { useNavigate } from "react-router-dom";
// import { motion } from "framer-motion";
// import {
//   ArrowLeft,
//   Dumbbell,
//   Clock,
//   Users,
//   CheckCircle2,
//   XCircle,
//   AlertCircle
// } from "lucide-react";
// import "../../assets/styles/GymStatus.css";

// export default function GymStatus() {
//   const navigate = useNavigate();

//   // 1. State for Gender (Toggle)
//   const [selectedGender, setSelectedGender] = useState("Male");

//   // 2. State for Time & Status
//   const [now, setNow] = useState(new Date());
//   const [status, setStatus] = useState({ isOpen: false, message: "", nextSlot: "" });

//   // 3. Live Clock Update
//   useEffect(() => {
//     const timer = setInterval(() => setNow(new Date()), 1000);
//     return () => clearInterval(timer);
//   }, []);

//   // 4. The CORE Logic (حساب الحالة)
//   useEffect(() => {
//     const calculateStatus = () => {
//       const day = now.getDay(); // 0 = Sunday, 5 = Friday
//       const hour = now.getHours();

//       // الجمعة مغلق دائماً
//       if (day === 5) {
//         setStatus({
//           isOpen: false,
//           message: "Weekend Break (Friday)",
//           nextSlot: "Opens Saturday 7:00 AM"
//         });
//         return;
//       }

//       // فترات اليوم (Slots)
//       let isOpen = false;
//       let message = "Currently Closed";
//       let nextSlot = "";

//       if (hour >= 7 && hour < 12) {
//         // الفترة الصباحية (رجالي)
//         if (selectedGender === "Male") {
//           isOpen = true;
//           message = "Morning Session (Men)";
//           nextSlot = "Closes at 12:00 PM";
//         } else {
//           isOpen = false;
//           message = "Men's Time Only";
//           nextSlot = "Women start at 12:00 PM";
//         }
//       } else if (hour >= 12 && hour < 16) {
//         // فترة الظهيرة (نسائي)
//         if (selectedGender === "Female") {
//           isOpen = true;
//           message = "Ladies Session";
//           nextSlot = "Closes at 4:00 PM";
//         } else {
//           isOpen = false;
//           message = "Women's Time Only";
//           nextSlot = "Men start at 4:00 PM";
//         }
//       } else if (hour >= 16 && hour < 22) {
//         // الفترة المسائية (رجالي)
//         if (selectedGender === "Male") {
//           isOpen = true;
//           message = "Evening Session (Men)";
//           nextSlot = "Closes at 10:00 PM";
//         } else {
//           isOpen = false;
//           message = "Men's Time Only";
//           nextSlot = "Opens Tomorrow 12:00 PM";
//         }
//       } else {
//         // مغلق (ليل متأخر أو فجر)
//         isOpen = false;
//         message = "Gym is Closed";
//         nextSlot = "Opens Tomorrow 7:00 AM";
//       }

//       setStatus({ isOpen, message, nextSlot });
//     };

//     calculateStatus();
//   }, [now, selectedGender]);

//   // Helper to format time nicely
//   const formatTime = (date) => {
//     return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
//   };

//   return (
//     <div className="gym-page-wrapper">
//       <div className="container">

//         {/* Header (Back Button) */}
//         <div className="d-flex align-items-center mb-5">
//           <button
//             onClick={() => navigate(-1)}
//             className="btn btn-link text-white p-0 me-3"
//             style={{ textDecoration: 'none' }}
//           >
//             <ArrowLeft size={28} />
//           </button>
//           <h2 className="m-0 fw-bold title-gradient" style={{ fontSize: '1.8rem' }}>
//             Gym Status
//           </h2>
//         </div>

//         {/* Gender Toggle Switch */}
//         <div className="gender-toggle">
//           <button
//             className={`toggle-btn ${selectedGender === 'Male' ? 'active' : ''}`}
//             onClick={() => setSelectedGender('Male')}
//           >
//             <Dumbbell size={18} /> Men
//           </button>
//           <button
//             className={`toggle-btn ${selectedGender === 'Female' ? 'active' : ''}`}
//             onClick={() => setSelectedGender('Female')}
//           >
//             <Users size={18} /> Women
//           </button>
//         </div>

//         {/* --- MAIN STATUS RING (ANIMATED) --- */}
//         <motion.div
//           className="status-ring-container"
//           initial={{ scale: 0.8, opacity: 0 }}
//           animate={{ scale: 1, opacity: 1 }}
//           transition={{ duration: 0.5 }}
//         >
//           <div className={`status-ring ${status.isOpen ? 'open' : 'closed'}`}></div>
//           <div className="status-content">
//             <span className="status-icon">
//               {status.isOpen ? (
//                 <CheckCircle2 size={60} color="#10b981" />
//               ) : (
//                 <XCircle size={60} color="#ef4444" />
//               )}
//             </span>
//             <div className="status-text" style={{ color: status.isOpen ? '#10b981' : '#ef4444' }}>
//               {status.isOpen ? "OPEN" : "CLOSED"}
//             </div>
//             <div className="status-timer">
//               {formatTime(now)}
//             </div>
//           </div>
//         </motion.div>

//         {/* Status Message */}
//         <div className="text-center mb-5">
//           <h3 className="text-white fw-bold">{status.message}</h3>
//           <p className="text-secondary d-flex align-items-center justify-content-center gap-2">
//             <Clock size={16} /> {status.nextSlot}
//           </p>
//         </div>

//         {/* --- SCHEDULE TIMELINE --- */}
//         <div className="schedule-container">
//           <h5 className="text-secondary mb-3 ps-2 small text-uppercase fw-bold tracking-wider">
//             Today's Schedule
//           </h5>

//           {/* Slot 1: 7am - 12pm (Men) */}
//           <ScheduleCard
//             start={7} end={12}
//             label="Morning Session"
//             gender="Men"
//             currentHour={now.getHours()}
//             day={now.getDay()}
//           />

//           {/* Slot 2: 12pm - 4pm (Women) */}
//           <ScheduleCard
//             start={12} end={16}
//             label="Afternoon Session"
//             gender="Women"
//             currentHour={now.getHours()}
//             day={now.getDay()}
//           />

//           {/* Slot 3: 4pm - 10pm (Men) */}
//           <ScheduleCard
//             start={16} end={22}
//             label="Evening Session"
//             gender="Men"
//             currentHour={now.getHours()}
//             day={now.getDay()}
//           />

//           {/* Friday Note */}
//           {now.getDay() === 5 && (
//             <div className="alert alert-danger d-flex align-items-center bg-danger bg-opacity-10 border-danger border-opacity-25 text-danger mt-3 rounded-4">
//               <AlertCircle size={20} className="me-2" />
//               <div>Gym is closed all day on Fridays.</div>
//             </div>
//           )}
//         </div>

//       </div>
//     </div>
//   );
// }

// // Sub-component for clean code
// function ScheduleCard({ start, end, label, gender, currentHour, day }) {
//   const formatHour = (h) => (h > 12 ? h - 12 : h) + (h >= 12 ? " PM" : " AM");
//   const isActive = day !== 5 && currentHour >= start && currentHour < end;

//   return (
//     <div className={`schedule-card ${isActive ? 'active-slot' : ''}`}>
//       <div className="d-flex align-items-center gap-3">
//         <div className={`p-2 rounded-circle ${isActive ? 'bg-primary text-white' : 'bg-secondary bg-opacity-10 text-secondary'}`}>
//           <Clock size={20} />
//         </div>
//         <div>
//           <div className="time-box">
//             {formatHour(start)} - {formatHour(end)}
//           </div>
//           <div className="small text-secondary">{label}</div>
//         </div>
//       </div>
//       <div className={`slot-tag ${gender === 'Men' ? 'men' : 'women'}`}>
//         {gender}
//       </div>
//     </div>
//   );
// }
